// ProcureAI web app ("Public Procurement Tools (Pluto)" to its users): session
// gate, the two frames, and the route tables.
//
//   guest          no session. "/" is the guest chat in the ChatShell (5
//                  messages a day, nothing stored). Any other user-app path
//                  goes to /masuk?next=, an admin path to /portal?next=.
//   sign-in        /masuk: Lanjutkan dengan Google only (simulated).
//                  /portal: email and password; linked from no public page.
//   pending        a new Google account: every path goes to /selamat-datang
//                  (onboarding) until the agreement and the role are done.
//   agreement due  any other non-admin who owes the current User Agreement
//                  (/auth/me agreementRequired, or a 403 agreement_required
//                  from any call): /selamat-datang?next=<the page> shows the
//                  agreement step only, then returns to that page.
//   user app       pa_kpa, ppk, pokja, pejabat_pengadaan, penyedia in the
//                  ChatShell: Asisten AI ("/", "/asisten/:id"), Library
//                  ("/pengetahuan"), E-Learning ("/belajar"), Profil, a
//                  project ("/project/:id", from the sidebar, and its
//                  conversations at "/project/:id/c/:convId"), and
//                  /dokumen/:id (opened from links in the chat), Regulasi
//                  ("/regulasi") and Template ("/template"); a Penyedia
//                  also has Dashboard ("/dasbor"), Pantau Paket
//                  ("/pantau-paket") and Alur Kerja ("/alur-kerja"); a
//                  Non-Penyedia role Dashboard PBJ, Alur PBJ and the PBJ
//                  Project page ("/project-pbj"). /beranda and
//                  /buat redirect to "/". Routes carry their role feature; one
//                  switched off shows Fitur tidak tersedia.
//   admin console  admin only, everything under /admin in shell/Shell.tsx.
//   konsultan      a konsultan account (POC, made by the admin in Akun):
//                  Pendampingan at /pendampingan (its assigned requests,
//                  each with the project read-only), Regulasi and Profil, in
//                  the same Shell; it signs in at /portal. Every other path goes to
//                  /pendampingan.
// An admin on any user-app path is sent to /admin; any other role on an
// /admin path is sent to "/".
import { useEffect, type ReactNode } from "react";
import type { Feature, Role, User } from "./api";
import { AuthProvider, owesAgreement, useAuth, type AuthState } from "./auth";
import { resetCatalog } from "./lib/catalog";
import { PRODUCT_NAME } from "./lib/product";
import { interceptLinks, matchRoute, navigate, useLocation } from "./router";
import { ChatShell } from "./shell/ChatShell";
import { FeatureProvider, useFeatures } from "./shell/features";
import { DisabledScreen } from "./shell/AccountBanners";
import { isActiveAccount, PAID_FEATURES } from "./lib/account";
import { Shell } from "./shell/Shell";
import { ADMIN_BASE, ADMIN_HOME, isAdminPath, isKonsultanPath, KONSULTAN_BASE, LOGIN_PATH, ONBOARDING_PATH, PBJ_DASHBOARD_PATH, PBJ_FLOW_PATH, PBJ_PROJECTS_PATH, PENYEDIA_PROJECTS_PATH, PORTAL_PATH, REGULATIONS_PATH } from "./shell/nav";
import { LoginScreen } from "./screens/Login";
import { PortalScreen } from "./screens/Portal";
import { OnboardingScreen } from "./screens/Onboarding";
import { ChatScreen } from "./screens/Chat";
import type { PageLayout } from "./ui/components";
import { DocumentsScreen } from "./screens/Documents";
import { DocumentDetailScreen } from "./screens/DocumentDetail";
import { LibraryScreen } from "./screens/Library";
import { LearningScreen } from "./screens/Learning";
import { ProfileScreen } from "./screens/Profile";
import { ProjectScreen } from "./screens/Project";
import { RegulationsScreen } from "./screens/Regulations";
import { TemplatesScreen } from "./screens/Templates";
import { AlurPbjScreen } from "./screens/AlurPbj";
import { DashboardPbjScreen } from "./screens/DashboardPbj";
import { ProjectsPbjScreen } from "./screens/ProjectsPbj";
import { NON_PENYEDIA_ROLES } from "./lib/labels";
import { FeatureOffScreen, ForbiddenScreen, InactiveFeatureScreen, NotFoundScreen } from "./screens/Status";
import { AccountsScreen } from "./admin/Accounts";
import { DatabaseScreen } from "./admin/Database";
import { KnowledgeAdminScreen } from "./admin/Knowledge";
import { UpdateKnowledgeScreen } from "./admin/UpdateKnowledge";
import { KnowledgeEditorScreen } from "./admin/KnowledgeEditor";
import { KnowledgeFileScreen } from "./admin/KnowledgeFile";
import { ElearningAdminScreen } from "./admin/Elearning";
import { ElearningEditorScreen } from "./admin/ElearningEditor";
import { RoleFeaturesScreen } from "./admin/RoleFeatures";
import { AgreementsScreen } from "./admin/Agreements";
import { KnowledgeSourceScreen } from "./admin/KnowledgeSource";
import { PendampinganAdminScreen } from "./admin/Pendampingan";
import { LoginGoogleScreen } from "./admin/LoginGoogle";
import { PlutoDriveScreen } from "./admin/PlutoDrive";
import { AssignedDetailScreen, AssignedListScreen } from "./screens/Pendampingan";
import { ProjectsPenyediaScreen } from "./penyedia/ProjectsPenyedia";
import { ReviewDokumenScreen } from "./screens/ReviewDokumen";
import { DataPerusahaanScreen } from "./screens/DataPerusahaan";
import { CompanyGroupsAdminScreen } from "./admin/CompanyGroups";
import { DATA_PERUSAHAAN_PATH, REVIEW_DOKUMEN_PATH } from "./shell/nav";

export { navFor, barSplit } from "./shell/nav";

interface RouteDef {
  pattern: string;
  nav: string;
  roles?: Role[];
  /** The role feature (GET /access/features) the route needs. */
  feature?: Feature;
  /** The screen fills the shell and scrolls its own regions (Workspace: Chat, Project). */
  fill?: boolean;
  /** The page width (Fase 4): "form" caps it at 760px (Review Dokumen, Data Perusahaan, Profil); a list page is 1200px. */
  layout?: PageLayout;
  render: (params: Record<string, string>) => ReactNode;
}

/** E-Learning is for every user-app role; the admin maintains it in the console. */
const LEARNERS: Role[] = ["pa_kpa", "ppk", "pokja", "pejabat_pengadaan", "tim_teknis", "tim_swakelola", "penyedia"];

/** The Penyedia menus (migration 0022) are for the Penyedia role only. */
const PENYEDIA: Role[] = ["penyedia"];

/** Old user-app paths that now open the chat (plan C9). */
export const RETIRED_PATHS = ["/beranda", "/buat"];

const FEATURE_NAME: Record<string, string> = {
  chat: "Chat",
  library: "Dokumen",
  elearning: "E-Learning",
  projects: "Project",
  document_generation: "Pembuatan dokumen",
  pantau_paket: "Pantau Paket",
  alur_kerja: "Alur Kerja",
  dashboard_penyedia: "Dashboard",
  regulations: "Regulasi",
  templates: "Generate Dokumen",
  pbj_flow: "Alur PBJ",
  pbj_dashboard: "Dashboard PBJ",
  company_data: "Data Perusahaan",
  document_review: "Review Dokumen",
};

/** The PBJ pages are Non-Penyedia only (migration 0023). */
const PBJ_ROLES: Role[] = [...NON_PENYEDIA_ROLES];

export const ROUTES: RouteDef[] = [
  { pattern: "/", nav: "asisten", feature: "chat", fill: true, render: () => <ChatScreen /> },
  { pattern: "/asisten/:id", nav: "asisten", feature: "chat", fill: true, render: (p) => <ChatScreen conversationId={p.id!} /> },
  // Reached from links in the chat only; nothing in the menu opens them.
  { pattern: "/dokumen", nav: "dokumen", render: () => <DocumentsScreen /> },
  { pattern: "/dokumen/:id", nav: "dokumen", render: (p) => <DocumentDetailScreen id={p.id!} /> },
  // A project opens from its sidebar row; an id not in the list is not found.
  { pattern: "/project/:id", nav: "project", feature: "projects", fill: true, render: (p) => <ProjectScreen id={p.id!} /> },
  // A conversation inside a project: the thread with the project's files beside it.
  { pattern: "/project/:id/c/:convId", nav: "project", feature: "projects", fill: true, render: (p) => <ProjectScreen id={p.id!} conversationId={p.convId!} /> },
  // Library: the user's own documents (the Basis Pengetahuan is a tab of Regulasi).
  { pattern: "/pengetahuan", nav: "pengetahuan", feature: "library", render: () => <LibraryScreen /> },
  { pattern: "/belajar", nav: "belajar", roles: LEARNERS, feature: "elearning", render: () => <LearningScreen /> },
  // Shared features (plan C1, A8, B6): Pusat Regulasi and the template library (each side its own templates).
  { pattern: "/regulasi", nav: "regulasi", feature: "regulations", render: () => <RegulationsScreen /> },
  { pattern: "/template", nav: "template", roles: LEARNERS, feature: "templates", render: () => <TemplatesScreen /> },
  { pattern: PBJ_DASHBOARD_PATH, nav: "dashboard-pbj", roles: PBJ_ROLES, feature: "pbj_dashboard", render: () => <DashboardPbjScreen /> },
  { pattern: PBJ_PROJECTS_PATH, nav: "project", roles: PBJ_ROLES, feature: "projects", render: () => <ProjectsPbjScreen /> },
  { pattern: PBJ_FLOW_PATH, nav: "alur-pbj", roles: PBJ_ROLES, feature: "pbj_flow", render: () => <AlurPbjScreen /> },
  { pattern: "/profil", nav: "profil", layout: "form", render: () => <ProfileScreen /> },
  // Data Perusahaan: company documents and the company group (optional for the user).
  { pattern: DATA_PERUSAHAAN_PATH, nav: "data-perusahaan", roles: LEARNERS, feature: "company_data", layout: "form", render: () => <DataPerusahaanScreen /> },
  // Review Dokumen (migration 0044): upload or paste a document, Pluto reviews it; the history of reviews.
  { pattern: REVIEW_DOKUMEN_PATH, nav: "review-dokumen", roles: LEARNERS, feature: "document_review", layout: "form", render: () => <ReviewDokumenScreen /> },
  // Project Penyedia (Simulasi Pluto v4): the paket projects and Temukan Paket. The old
  // Dashboard, Pantau Paket and Alur Kerja paths open it too.
  { pattern: PENYEDIA_PROJECTS_PATH, nav: "project-penyedia", roles: PENYEDIA, feature: "projects", render: () => <ProjectsPenyediaScreen /> },
  { pattern: "/dasbor", nav: "project-penyedia", roles: PENYEDIA, feature: "projects", render: () => <ProjectsPenyediaScreen /> },
  { pattern: "/pantau-paket", nav: "project-penyedia", roles: PENYEDIA, feature: "projects", render: () => <ProjectsPenyediaScreen /> },
  { pattern: "/alur-kerja", nav: "project-penyedia", roles: PENYEDIA, feature: "projects", render: () => <ProjectsPenyediaScreen /> },
];

/** The admin console. "/admin" itself only redirects to Akun. */
export const ADMIN_ROUTES: RouteDef[] = [
  { pattern: "/admin/akun", nav: "akun", render: () => <AccountsScreen /> },
  { pattern: "/admin/pengetahuan", nav: "pengetahuan", render: () => <KnowledgeAdminScreen /> },
  { pattern: "/admin/pengetahuan/baru", nav: "pengetahuan", render: () => <KnowledgeEditorScreen /> },
  { pattern: "/admin/pengetahuan/:file", nav: "pengetahuan", render: (p) => <KnowledgeFileScreen file={p.file!} /> },
  { pattern: "/admin/pengetahuan/:file/ganti", nav: "pengetahuan", render: (p) => <KnowledgeEditorScreen file={p.file!} /> },
  { pattern: "/admin/update-knowledge", nav: "updateknowledge", render: () => <UpdateKnowledgeScreen /> },
  { pattern: "/admin/basis-data", nav: "basisdata", render: () => <DatabaseScreen /> },
  { pattern: "/admin/fitur-role", nav: "fitur", render: () => <RoleFeaturesScreen /> },
  { pattern: "/admin/persetujuan", nav: "persetujuan", render: () => <AgreementsScreen /> },
  { pattern: "/admin/sumber-pengetahuan", nav: "sumber", render: () => <KnowledgeSourceScreen /> },
  { pattern: "/admin/elearning", nav: "elearning", render: () => <ElearningAdminScreen /> },
  { pattern: "/admin/elearning/baru", nav: "elearning", render: () => <ElearningEditorScreen /> },
  { pattern: "/admin/elearning/:courseId", nav: "elearning", render: (p) => <ElearningEditorScreen courseId={p.courseId!} /> },
  { pattern: "/admin/pendampingan", nav: "pendampingan", render: () => <PendampinganAdminScreen /> },
  { pattern: "/admin/login-google", nav: "logingoogle", render: () => <LoginGoogleScreen /> },
  { pattern: "/admin/penyimpanan-pluto", nav: "penyimpanan", render: () => <PlutoDriveScreen /> },
  { pattern: "/admin/grup-perusahaan", nav: "grupperusahaan", render: () => <CompanyGroupsAdminScreen /> },
  { pattern: "/admin/profil", nav: "profil", layout: "form", render: () => <ProfileScreen /> },
];

/** The konsultan app (POC): its assigned requests and Profil. */
export const KONSULTAN_ROUTES: RouteDef[] = [
  { pattern: "/pendampingan", nav: "pendampingan", render: () => <AssignedListScreen /> },
  { pattern: "/pendampingan/:id", nav: "pendampingan", render: (p) => <AssignedDetailScreen id={p.id!} /> },
  { pattern: "/regulasi", nav: "regulasi", render: () => <RegulationsScreen /> },
  { pattern: "/profil", nav: "profil", layout: "form", render: () => <ProfileScreen /> },
];

export function resolveRoute(path: string, role?: Role): { route: RouteDef; params: Record<string, string> } | null {
  for (const route of role === "konsultan" ? KONSULTAN_ROUTES : isAdminPath(path) ? ADMIN_ROUTES : ROUTES) {
    const params = matchRoute(route.pattern, path);
    if (params) return { route, params };
  }
  return null;
}

/**
 * Where a signed-in user on `path` must go instead, or null to stay.
 * A pending account lives on onboarding; admins live in the console; every
 * other role lives in the user app, where the retired paths open the chat.
 */
export function redirectFor(role: Role, path: string): string | null {
  if (role === "pending") return path === ONBOARDING_PATH ? null : ONBOARDING_PATH;
  if (role === "admin") {
    if (path === ADMIN_BASE) return ADMIN_HOME;
    return isAdminPath(path) ? null : ADMIN_BASE;
  }
  if (role === "konsultan") return isKonsultanPath(path) || path === "/profil" || path === REGULATIONS_PATH ? null : KONSULTAN_BASE;
  if (isAdminPath(path)) return "/";
  if (path === ONBOARDING_PATH || RETIRED_PATHS.includes(path)) return "/";
  return null;
}

/**
 * Where a signed-out visitor on `href` must go instead, or null to stay.
 * "/" (the guest chat) and the two sign-in pages stay; the admin console
 * signs in at /portal, every other page at /masuk, both with ?next=.
 */
export function anonRedirectFor(path: string, href: string): string | null {
  if (path === "/" || path === LOGIN_PATH || path === PORTAL_PATH) return null;
  if (RETIRED_PATHS.includes(path)) return "/";
  // The console and the konsultan app sign in with a password at /portal.
  const target = isAdminPath(path) || isKonsultanPath(path) ? PORTAL_PATH : LOGIN_PATH;
  return `${target}?next=${encodeURIComponent(href)}`;
}

/**
 * Where a user who owes the current User Agreement must go, or null to stay:
 * the agreement step, with ?next= back to the page they were on (from a
 * sign-in page, its own ?next=; "/" needs no ?next=).
 */
export function agreementRedirectFor(path: string, href: string, signInNext: string | null = null): string | null {
  if (path === ONBOARDING_PATH) return null;
  const back = path === LOGIN_PATH || path === PORTAL_PATH ? signInNext : href;
  return back && back !== "/" && !back.startsWith(ONBOARDING_PATH) ? `${ONBOARDING_PATH}?next=${encodeURIComponent(back)}` : ONBOARDING_PATH;
}

/** A safe same-origin ?next= target, or null. */
function safeNext(next: string | null): string | null {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : null;
}

function Splash() {
  return (
    <main className="splash" data-ui-shell="none" data-jal-shell="none" aria-busy="true">
      <p className="splash-text" role="status">
        Memuat {PRODUCT_NAME}
      </p>
    </main>
  );
}

const ADMIN_BACK = { href: ADMIN_HOME, label: "Kembali ke Akun" };

export function Routes() {
  const { state } = useAuth();
  const loc = useLocation();

  const onSignIn = loc.path === LOGIN_PATH || loc.path === PORTAL_PATH;
  // A deactivated account sees one screen with Keluar, whatever the path.
  const disabled = state.status === "authed" && state.user.role !== "admin" && state.user.status === "nonaktif";
  const owes = owesAgreement(state) && !disabled;
  let redirect: string | null = null;
  if (disabled) redirect = null;
  else if (state.status === "anon") redirect = anonRedirectFor(loc.path, loc.href);
  else if (owes) redirect = agreementRedirectFor(loc.path, loc.href, onSignIn ? safeNext(loc.query.get("next")) : null);
  else if (state.status === "authed") {
    redirect = onSignIn ? safeNext(loc.query.get("next")) ?? "/" : redirectFor(state.user.role, loc.path);
    // Signing in from a page whose ?next= the role may not open lands on its home.
    if (onSignIn && redirect) redirect = redirectFor(state.user.role, redirect.split("?")[0]!) ?? redirect;
    // A new account keeps the page it was going to (a guest's menu row, a
    // deep link) through onboarding, as ?next=.
    if (state.user.role === "pending" && redirect === ONBOARDING_PATH) redirect = agreementRedirectFor(loc.path, loc.href, onSignIn ? safeNext(loc.query.get("next")) : null);
  }

  useEffect(() => {
    if (redirect) navigate(redirect, { replace: true });
  }, [redirect]);

  if (state.status === "loading" || redirect) return <Splash />;

  if (state.status === "anon") {
    if (loc.path === LOGIN_PATH) return <LoginScreen expired={state.expired === true} />;
    if (loc.path === PORTAL_PATH) return <PortalScreen expired={state.expired === true} />;
    return (
      <FeatureProvider user={null}>
        <ChatShell user={null} current="asisten" path={loc.path} fill>
          <ChatScreen guest />
        </ChatShell>
      </FeatureProvider>
    );
  }

  const user = state.user;
  if (disabled) return <DisabledScreen />;
  if (user.role === "pending") return <OnboardingScreen next={safeNext(loc.query.get("next")) ?? "/"} />;
  if (owes) return <OnboardingScreen agreementOnly next={safeNext(loc.query.get("next")) ?? "/"} />;
  if (user.role === "admin") return <AdminApp path={loc.path} user={user} />;
  if (user.role === "konsultan") return <KonsultanApp path={loc.path} user={user} />;
  return (
    <FeatureProvider user={user}>
      <UserApp path={loc.path} />
    </FeatureProvider>
  );
}

function AdminApp({ path, user }: { path: string; user: User }) {
  const hit = resolveRoute(path);
  if (!hit) {
    return (
      <Shell user={user} current="" path={path}>
        <NotFoundScreen home={ADMIN_BACK} />
      </Shell>
    );
  }
  const allowed = !hit.route.roles || hit.route.roles.includes(user.role);
  return (
    <Shell user={user} current={hit.route.nav} path={path} fill={allowed && hit.route.fill} layout={allowed ? hit.route.layout : undefined}>
      {allowed ? hit.route.render(hit.params) : <ForbiddenScreen role={user.role} home={ADMIN_BACK} />}
    </Shell>
  );
}

function KonsultanApp({ path, user }: { path: string; user: User }) {
  const hit = resolveRoute(path, "konsultan");
  if (!hit) {
    return (
      <Shell user={user} current="" path={path}>
        <NotFoundScreen home={{ href: KONSULTAN_BASE, label: "Kembali ke Pendampingan" }} />
      </Shell>
    );
  }
  return (
    <Shell user={user} current={hit.route.nav} path={path} layout={hit.route.layout}>
      {hit.route.render(hit.params)}
    </Shell>
  );
}

function UserApp({ path }: { path: string }) {
  const { state } = useAuth();
  const features = useFeatures();
  if (state.status !== "authed") return null;
  const user = state.user;
  const hit = resolveRoute(path);
  if (!hit) {
    return (
      <ChatShell user={user} current="" path={path}>
        <NotFoundScreen />
      </ChatShell>
    );
  }
  const allowed = !hit.route.roles || hit.route.roles.includes(user.role);
  const featureOn = features.has(hit.route.feature);
  const ok = allowed && featureOn;
  let body: ReactNode;
  if (!allowed) body = <ForbiddenScreen role={user.role} />;
  else if (!featureOn && hit.route.feature && PAID_FEATURES.includes(hit.route.feature) && !isActiveAccount(user)) body = <InactiveFeatureScreen name={FEATURE_NAME[hit.route.feature] ?? "Fitur ini"} />;
  else if (!featureOn) {
    const home = hit.route.feature === "chat" ? { href: "/profil", label: "Buka Profil" } : undefined;
    body = <FeatureOffScreen role={user.role} name={FEATURE_NAME[hit.route.feature ?? ""] ?? "Fitur ini"} home={home} />;
  } else body = hit.route.render(hit.params);
  return (
    <ChatShell user={user} current={hit.route.nav} path={path} fill={ok && hit.route.fill} layout={ok ? hit.route.layout : undefined}>
      {body}
    </ChatShell>
  );
}

export function App({ initialAuth }: { initialAuth?: AuthState } = {}) {
  useEffect(() => interceptLinks(document), []);
  useEffect(() => () => resetCatalog(), []);
  return (
    <AuthProvider initial={initialAuth}>
      <Routes />
    </AuthProvider>
  );
}
